import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Sprout } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

export const Route = createFileRoute('/upgrade/success')({
  validateSearch: (search): { checkout_id?: string; plan?: string } => ({
    checkout_id: typeof search.checkout_id === 'string' ? search.checkout_id : undefined,
    plan: typeof search.plan === 'string' ? search.plan : undefined,
  }),
  component: UpgradeSuccessPage,
})

function UpgradeSuccessPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { checkout_id, plan } = Route.useSearch()
  const lifetime = plan === 'lifetime'
  const [countdown, setCountdown] = useState(4)

  useEffect(() => {
    if (countdown <= 0) {
      navigate({ to: '/today' })
      return
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000)
    return () => clearTimeout(timer)
  }, [countdown, navigate])

  return (
    <div className="flex min-h-svh flex-col items-center justify-center bg-background px-6 text-center">
      <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
        <Sprout className="size-10 text-primary-text" strokeWidth={1.75} />
      </div>

      <h1 className="mb-3 text-3xl font-black text-foreground">
        {lifetime
          ? t('upgrade.successLifetimeTitle')
          : t('upgrade.successTitle', { defaultValue: 'Welcome to Pro!' })}
      </h1>
      <p className="mb-2 max-w-sm text-base text-muted-foreground">
        {lifetime
          ? t('upgrade.successLifetimeBody')
          : t('upgrade.successBody', {
              defaultValue: 'Your subscription is active. All Pro features are unlocked.',
            })}
      </p>

      {checkout_id && (
        <p className="mb-6 text-xs text-muted-foreground/40">
          Order #{checkout_id.slice(0, 8).toUpperCase()}
        </p>
      )}

      <p className="mb-8 text-sm text-muted-foreground/60">
        {t('upgrade.successRedirect', {
          defaultValue: 'Redirecting to your goals in {{count}}s…',
          count: countdown,
        })}
      </p>

      <button
        type="button"
        onClick={() => navigate({ to: '/today' })}
        className="cursor-pointer rounded-full bg-primary px-7 py-3 text-sm font-bold text-primary-foreground transition-all hover:bg-primary/90"
      >
        {t('upgrade.successCta', { defaultValue: 'Start now →' })}
      </button>
    </div>
  )
}
